/**
 * MMO Network Manager
 * Handles WebSocket communication with MMO backend server
 *
 * Uses DisposableGroup for automatic cleanup of event listeners and timers.
 */

import type { HexCoord } from '../shared/HexUtils';
import type { MMOWorldStatePayload } from './MMOStateManager';
import { EventBus } from '../utils/EventBus';
import { getWebSocketUrl } from '../utils/ApiConfig';
import { DisposableGroup, IDisposable } from '../shared/common/lifecycle';

// Use environment-aware WebSocket URL (never hardcode localhost!)
const DEFAULT_MMO_SERVER_URL = getWebSocketUrl();
const RECONNECT_DELAY = 3000;
const PING_INTERVAL = 30000;
const MAX_RECONNECT_ATTEMPTS = 10;
const PONG_WATCHDOG_TIMEOUT = 20000;

// MMO Events
export const MMOEvents = {
  CONNECTED: 'mmo-connected',
  DISCONNECTED: 'mmo-disconnected',
  AUTHENTICATED: 'mmo-authenticated',
  WORLD_JOINED: 'mmo-world-joined',
  WORLD_STATE: 'mmo-world-state',
  UNIT_MOVED: 'mmo-unit-moved',
  UNIT_BUILT: 'mmo-unit-built',
  BUILD_QUEUED: 'mmo-build-queued',
  BUILD_PROGRESS: 'mmo-build-progress',
  BUILD_COMPLETE: 'mmo-build-complete',
  COMBAT_RESULT: 'mmo-combat-result',
  ALLIANCE_CREATED: 'mmo-alliance-created',
  ALLIANCE_LEFT: 'mmo-alliance-left',
  ALLIANCE_CHAT: 'mmo-alliance-chat',
  GLOBAL_CHAT: 'mmo-global-chat',
  ALLIANCE_LIST: 'mmo-alliance-list',
  ALLIANCE_JOINED: 'mmo-alliance-joined',
  ALLIANCE_RENAMED: 'mmo-alliance-renamed',
  ALLIANCE_DETAILS: 'mmo-alliance-details',
  DIRECT_MESSAGE: 'mmo-direct-message',
  TICK_UPDATE: 'mmo-tick-update',
  ABILITY_ACTIVATED: 'mmo-ability-activated',
  ABILITY_ERROR: 'mmo-ability-error',
  STATUS_EFFECT_APPLIED: 'mmo-status-effect-applied',
  STATUS_EFFECT_EXPIRED: 'mmo-status-effect-expired',
  PURCHASE_CONFIRMED: 'mmo-purchase-confirmed',
  PURCHASE_FAILED: 'mmo-purchase-failed',
  CREW_XP_GAINED: 'mmo-crew-xp-gained',
  MINE_RESULT: 'mmo-mine-result',
  MINE_STARTED: 'mmo-mine-started',
  MINE_STOPPED: 'mmo-mine-stopped',
  CARGO_UNLOADED: 'mmo-cargo-unloaded',
  CHAT_MESSAGE: 'mmo-chat-message',
  PLAYER_RESOURCES: 'mmo-player-resources',
  CHUNK_DATA: 'mmo-chunk-data',
  ENGAGEMENT_STARTED: 'mmo-engagement-started',
  ENGAGEMENT_ENDED: 'mmo-engagement-ended',
  DISENGAGE_SUCCESS: 'mmo-disengage-success',
  REPAIR_RESULT: 'mmo-repair-result',
  TRANSFER_COMPLETE: 'mmo-transfer-complete',
  TRANSFER_STARTED: 'mmo-transfer-started',
  TRANSFER_CANCELLED: 'mmo-transfer-cancelled',
  MINERALS_DEPOSITED: 'mmo-minerals-deposited',
  MINERALS_WITHDRAWN: 'mmo-minerals-withdrawn',
  CARGO_LOADED: 'mmo-cargo-loaded',
  STATION_WRECK_CREATED: 'mmo-station-wreck-created',
  STATION_WRECK_SCAVENGED: 'mmo-station-wreck-scavenged',
  STATION_REPAIRED: 'mmo-station-repaired',
  RELIC_PICKED_UP: 'mmo-relic-picked-up',
  SHIPYARD_TOGGLED: 'mmo-shipyard-toggled',
  SHIPYARD_INFO: 'mmo-shipyard-info',
  SHIP_MOD_RESULT: 'mmo-ship-mod-result',
  LOCATION_COMPONENTS: 'mmo-location-components',
  SHIP_COMPONENTS: 'mmo-ship-components',
  SCAN_RESULT: 'mmo-scan-result',
  PLANET_SCAN_DATA: 'mmo-planet-scan-data',
  STATION_MINE_STARTED: 'mmo-station-mine-started',
  STATION_MINE_STOPPED: 'mmo-station-mine-stopped',
  STATION_MINE_RESULT: 'mmo-station-mine-result',
  FTUE_GUIDANCE: 'mmo-ftue-guidance',
  FTUE_STAGE_UPDATE: 'mmo-ftue-stage-update',
  FTUE_COMPLETED: 'mmo-ftue-completed',
  LOOT_CLAIMED: 'mmo-loot-claimed',
  AMBIENT_SALVAGED: 'mmo-ambient-salvaged',
  AMBIENT_SCAN_RESULT: 'mmo-ambient-scan-result',
  UNIT_DESTROYED_NOTIFICATION: 'mmo-unit-destroyed-notification',
  LEADERBOARD_RESPONSE: 'mmo-leaderboard-response',
  TERRITORY_UPDATE: 'mmo-territory-update',
  DESIGNATE_HQ_RESULT: 'mmo-designate-hq-result',
  SET_TAX_RATE_RESULT: 'mmo-set-tax-rate-result',
  DEPLOY_STRUCTURE_RESULT: 'mmo-deploy-structure-result',
  REPAIR_STRUCTURE_RESULT: 'mmo-repair-structure-result',
  ERROR: 'mmo-error',
  RECONNECT_FAILED: 'mmo-reconnect-failed',
};

// Message type definitions
interface BaseMessage {
  type: string;
  payload?: any;
}

interface MMOWorldStateMessage extends BaseMessage {
  type: 'mmo_world_state';
  payload: MMOWorldStatePayload;
}

interface MMOUnitMovedMessage extends BaseMessage {
  type: 'mmo_unit_moved';
  payload: {
    unitId: string;
    position: HexCoord;
  };
}

interface MMOCombatResultMessage extends BaseMessage {
  type: 'mmo_combat_result';
  payload: {
    attackerId: string;
    defenderId: string;
    damage: number;
    defenderDestroyed: boolean;
  };
}

type MMOMessage = BaseMessage | MMOWorldStateMessage | MMOUnitMovedMessage | MMOCombatResultMessage;

export class MMONetworkManager implements IDisposable {
  private socket: WebSocket | null = null;
  private serverUrl: string = DEFAULT_MMO_SERVER_URL;
  private sessionToken: string | null = null;

  // Connection state
  public isConnected: boolean = false;
  public isAuthenticated: boolean = false;
  public disconnectReason: 'idle' | 'network' | null = null;
  private pendingRejoin: boolean = false;
  private reconnectAttempts: number = 0;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private pingInterval: NodeJS.Timeout | null = null;
  private pongWatchdog: NodeJS.Timeout | null = null;
  private connectionVersion: number = 0;

  // Message handlers
  private messageHandlers: Map<string, ((message: any) => void)[]> = new Map();

  // Lifecycle management
  private readonly disposables = new DisposableGroup();
  private _isDisposed = false;

  get isDisposed(): boolean {
    return this._isDisposed;
  }

  constructor(serverUrl?: string) {
    if (serverUrl) {
      this.serverUrl = serverUrl;
    }

    // Listen for generic send_message events from EventBus (for progression system)
    // Uses scoped cleanup - will be removed on dispose()
    const sendMessageHandler = (message: BaseMessage) => {
      this.send(message);
    };
    EventBus.on('send_message', sendMessageHandler);
    this.disposables.addCleanup(() => {
      EventBus.off('send_message', sendMessageHandler);
    }, 'event:send_message');

    // When the tab becomes visible again, verify the WebSocket is truly alive.
    // Browsers throttle/suspend background-tab timers, so:
    //   1. The 30s ping interval may have stopped, causing the server to
    //      terminate the connection after its 2-minute ping timeout.
    //   2. The onclose event may not have fired yet (browser suspended it).
    //   3. Even if onclose fired, the 5 reconnect attempts (3s apart) may
    //      have all been exhausted while the tab was throttled.
    // Fix: check the actual socket readyState, not just this.isConnected,
    // and always allow a fresh reconnect on tab focus.
    const visibilityHandler = () => {
      if (!document.hidden) {
        const socketAlive = this.socket && this.socket.readyState === WebSocket.OPEN;
        if (socketAlive) {
          // Socket looks open — send a ping and start a watchdog timer.
          // If no pong arrives within 5 seconds, the connection is dead.
          this.send({ type: 'ping' });
          this.startPongWatchdog();
        } else if (this.sessionToken) {
          // Socket is closed/closing/null — force reconnect regardless of
          // how many previous attempts were made in the background.
          console.log('[MMONetwork] Tab visible, socket not open — reconnecting...');
          // Clean up stale socket state
          this.isConnected = false;
          this.isAuthenticated = false;
          this.stopPingInterval();
          if (this.socket) {
            this.socket.onopen = null;
            this.socket.onclose = null;
            this.socket.onerror = null;
            this.socket.onmessage = null;
            try { this.socket.close(); } catch (_e) { /* ignore */ }
            this.socket = null;
          }
          this.reconnect();
        }
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);
    this.disposables.addCleanup(() => {
      document.removeEventListener('visibilitychange', visibilityHandler);
    }, 'event:visibilitychange');

    // Also listen for window focus — covers switching between browser windows
    // or returning from a new tab (visibilitychange may not fire if tab wasn't hidden)
    const focusHandler = () => {
      const socketAlive = this.socket && this.socket.readyState === WebSocket.OPEN;
      if (socketAlive) {
        this.send({ type: 'ping' });
        this.startPongWatchdog();
      } else if (this.sessionToken) {
        console.log('[MMONetwork] Window focused, socket not open — reconnecting...');
        this.isConnected = false;
        this.isAuthenticated = false;
        this.stopPingInterval();
        if (this.socket) {
          this.socket.onopen = null;
          this.socket.onclose = null;
          this.socket.onerror = null;
          this.socket.onmessage = null;
          try { this.socket.close(); } catch (_e) { /* ignore */ }
          this.socket = null;
        }
        this.reconnect();
      }
    };
    window.addEventListener('focus', focusHandler);
    this.disposables.addCleanup(() => {
      window.removeEventListener('focus', focusHandler);
    }, 'event:windowfocus');
  }

  /**
   * Connect to MMO server
   */
  public connect(sessionToken: string): void {
    if (this.socket && this.isConnected) {
      console.warn('[MMONetwork] Already connected');
      return;
    }

    this.sessionToken = sessionToken;
    this.attemptConnection();
  }

  /**
   * Attempt WebSocket connection
   */
  private attemptConnection(): void {
    console.log(`[MMONetwork] Connecting to ${this.serverUrl}...`);

    const version = ++this.connectionVersion;

    try {
      this.socket = new WebSocket(this.serverUrl);

      this.socket.onopen = () => this.handleOpen();
      this.socket.onclose = () => {
        // Ignore stale close events from old connections
        if (version !== this.connectionVersion) {
          console.log('[MMONetwork] Ignoring stale onclose callback');
          return;
        }
        this.handleClose();
      };
      this.socket.onerror = (error) => this.handleError(error);
      this.socket.onmessage = (event) => this.handleMessage(event);
    } catch (error) {
      console.error('[MMONetwork] Connection failed:', error);
      this.scheduleReconnect();
    }
  }

  /**
   * Handle WebSocket open
   */
  private handleOpen(): void {
    console.log('[MMONetwork] Connected to server');
    this.isConnected = true;
    this.reconnectAttempts = 0;

    // Emit connection event
    EventBus.emit(MMOEvents.CONNECTED);

    // Authenticate with session token
    if (this.sessionToken) {
      this.authenticate(this.sessionToken);
    }

    // Start ping interval
    this.startPingInterval();
  }

  /**
   * Handle WebSocket close
   */
  private handleClose(): void {
    console.log('[MMONetwork] Disconnected from server');
    this.isConnected = false;
    this.isAuthenticated = false;

    // Stop timers
    this.stopPingInterval();
    this.clearPongWatchdog();

    // Set disconnect reason if not already set (idle_disconnect sets it before close)
    if (this.disconnectReason !== 'idle') {
      this.disconnectReason = 'network';
    }

    // Emit disconnection event
    EventBus.emit(MMOEvents.DISCONNECTED);

    // Skip auto-reconnect for idle disconnects (player must click to reconnect)
    if (this.disconnectReason === 'idle') {
      return;
    }

    // Attempt reconnect for network disconnects
    this.scheduleReconnect();
  }

  /**
   * Handle WebSocket error
   */
  private handleError(error: Event): void {
    console.error('[MMONetwork] WebSocket error:', error);
    EventBus.emit(MMOEvents.ERROR, { error: 'Connection error' });
  }

  /**
   * Handle incoming WebSocket message
   */
  private handleMessage(event: MessageEvent): void {
    try {
      const message: MMOMessage = JSON.parse(event.data);

      // Handle pong — clear the watchdog timer (connection is alive)
      if (message.type === 'pong') {
        this.clearPongWatchdog();
        return;
      }

      // Handle idle disconnect notification
      if (message.type === 'idle_disconnect') {
        this.disconnectReason = 'idle';
        return;
      }

      // Handle authentication response
      if (message.type === 'auth_success') {
        this.isAuthenticated = true;
        EventBus.emit(MMOEvents.AUTHENTICATED, message.payload);
        // Auto-rejoin world after reconnect
        if (this.pendingRejoin) {
          this.pendingRejoin = false;
          this.sendJoinWorld(1);
        }
        return;
      }

      // Handle error messages
      // Server sends errors as { type: 'error', code: '...', message: '...' }
      // (fields at root level, not inside payload)
      if (message.type === 'error') {
        const errorPayload = {
          message: (message as any).message || 'Unknown server error',
          code: (message as any).code || 'SERVER_ERROR',
          ...((message.payload as Record<string, unknown> | undefined) || {}),
        };
        console.error('[MMONetwork] Server error:', errorPayload);
        EventBus.emit(MMOEvents.ERROR, errorPayload);
        return;
      }

      // Emit event for this message type
      const eventName = this.messageTypeToEvent(message.type);
      if (eventName) {
        EventBus.emit(eventName, message.payload);
      }

      // Call registered handlers
      const handlers = this.messageHandlers.get(message.type);
      if (handlers) {
        handlers.forEach((handler) => handler(message.payload));
      }
    } catch (error) {
      console.error('[MMONetwork] Failed to parse message:', error);
    }
  }

  /**
   * Convert message type to event name
   */
  private messageTypeToEvent(type: string): string | null {
    const eventMap: { [key: string]: string } = {
      mmo_world_state: MMOEvents.WORLD_STATE,
      mmo_unit_moved: MMOEvents.UNIT_MOVED,
      mmo_unit_built: MMOEvents.UNIT_BUILT,
      mmo_build_queued: MMOEvents.BUILD_QUEUED,
      mmo_build_progress: MMOEvents.BUILD_PROGRESS,
      mmo_build_complete: MMOEvents.BUILD_COMPLETE,
      mmo_combat_result: MMOEvents.COMBAT_RESULT,
      mmo_alliance_created: MMOEvents.ALLIANCE_CREATED,
      mmo_alliance_left: MMOEvents.ALLIANCE_LEFT,
      mmo_alliance_chat: MMOEvents.ALLIANCE_CHAT,
      mmo_alliance_message: MMOEvents.ALLIANCE_CHAT,
      mmo_chat_message: MMOEvents.GLOBAL_CHAT,
      mmo_alliance_list_response: MMOEvents.ALLIANCE_LIST,
      mmo_alliance_joined: MMOEvents.ALLIANCE_JOINED,
      mmo_alliance_renamed: MMOEvents.ALLIANCE_RENAMED,
      mmo_alliance_details_response: MMOEvents.ALLIANCE_DETAILS,
      mmo_direct_message: MMOEvents.DIRECT_MESSAGE,
      mmo_tick_update: MMOEvents.TICK_UPDATE,
      // Phase 3 progression messages
      mmo_ship_progression: 'mmo_ship_progression',
      mmo_specialization_set: 'mmo_specialization_set',
      mmo_perk_added: 'mmo_perk_added',
      mmo_ship_prestiged: 'mmo_ship_prestiged',
      mmo_player_progression: 'mmo_player_progression',
      mmo_player_resources: MMOEvents.PLAYER_RESOURCES,
      mmo_resources: 'mmo_player_resources',
      mmo_resources_transferred: 'mmo_resources_transferred',
      mmo_unit_components: 'mmo_unit_components',
      mmo_upgrade_started: 'mmo_upgrade_started',
      mmo_upgrade_cancelled: 'mmo_upgrade_cancelled',
      mmo_unit_docked: 'mmo_unit_docked',
      mmo_unit_undocked: 'mmo_unit_undocked',
      mmo_return_to_base_confirmed: 'mmo_return_to_base_confirmed',
      // Movement reroute: server fires this when the player-tapped hex is
      // blocked at tick time (planet/station/other ship moved in) and the
      // unit is redirected to the nearest open neighbor. The scene listens
      // so it can show a toast explaining why the ship went somewhere else.
      mmo_movement_rerouted: 'mmo_movement_rerouted',
      mmo_golden_asteroid_delivered: 'mmo_golden_asteroid_delivered',
      mmo_golden_asteroid_spawned: 'mmo_golden_asteroid_spawned',
      mmo_asteroid_split: 'mmo_asteroid_split',
      mmo_asteroid_warning: 'mmo_asteroid_warning',
      mmo_asteroid_destroyed: 'mmo_asteroid_destroyed',
      mmo_asteroid_depleted: 'mmo_asteroid_depleted',
      mmo_admin_asteroids_removed: 'mmo_admin_asteroids_removed',
      mmo_ability_activated: MMOEvents.ABILITY_ACTIVATED,
      mmo_ability_error: MMOEvents.ABILITY_ERROR,
      mmo_status_effect_applied: MMOEvents.STATUS_EFFECT_APPLIED,
      mmo_status_effect_expired: MMOEvents.STATUS_EFFECT_EXPIRED,
      // Purchase messages
      mmo_purchase_confirmed: MMOEvents.PURCHASE_CONFIRMED,
      mmo_purchase_failed: MMOEvents.PURCHASE_FAILED,
      // Crew messages
      mmo_crew_xp_gained: MMOEvents.CREW_XP_GAINED,
      mmo_mine_result: MMOEvents.MINE_RESULT,
      mmo_mine_started: MMOEvents.MINE_STARTED,
      mmo_mine_stopped: MMOEvents.MINE_STOPPED,
      mmo_cargo_unloaded: MMOEvents.CARGO_UNLOADED,
      mmo_chunk_data: MMOEvents.CHUNK_DATA,
      mmo_engagement_started: MMOEvents.ENGAGEMENT_STARTED,
      mmo_engagement_ended: MMOEvents.ENGAGEMENT_ENDED,
      mmo_disengage_success: MMOEvents.DISENGAGE_SUCCESS,
      mmo_repair_result: MMOEvents.REPAIR_RESULT,
      mmo_transfer_complete: MMOEvents.TRANSFER_COMPLETE,
      mmo_station_wreck_created: MMOEvents.STATION_WRECK_CREATED,
      mmo_station_wreck_scavenged: MMOEvents.STATION_WRECK_SCAVENGED,
      mmo_station_wreck_repaired: MMOEvents.STATION_REPAIRED,
      mmo_relic_picked_up: MMOEvents.RELIC_PICKED_UP,
      mmo_shipyard_toggled: MMOEvents.SHIPYARD_TOGGLED,
      mmo_shipyard_info: MMOEvents.SHIPYARD_INFO,
      mmo_ship_mod_result: MMOEvents.SHIP_MOD_RESULT,
      mmo_location_components: MMOEvents.LOCATION_COMPONENTS,
      mmo_ship_components: MMOEvents.SHIP_COMPONENTS,
      mmo_research_completed: 'mmo_research_completed',
      mmo_scan_result: MMOEvents.SCAN_RESULT,
      mmo_planet_scan_data: MMOEvents.PLANET_SCAN_DATA,
      mmo_station_mine_started: MMOEvents.STATION_MINE_STARTED,
      mmo_station_mine_stopped: MMOEvents.STATION_MINE_STOPPED,
      mmo_station_mine_result: MMOEvents.STATION_MINE_RESULT,
      ftue_guidance: MMOEvents.FTUE_GUIDANCE,
      ftue_stage_update: MMOEvents.FTUE_STAGE_UPDATE,
      ftue_completed: MMOEvents.FTUE_COMPLETED,
      mmo_loot_claimed: MMOEvents.LOOT_CLAIMED,
      mmo_ambient_salvaged: MMOEvents.AMBIENT_SALVAGED,
      mmo_ambient_scan_result: MMOEvents.AMBIENT_SCAN_RESULT,
      mmo_unit_destroyed: MMOEvents.UNIT_DESTROYED_NOTIFICATION,
      mmo_leaderboard_response: MMOEvents.LEADERBOARD_RESPONSE,
      mmo_territory_update: MMOEvents.TERRITORY_UPDATE,
      mmo_designate_hq_result: MMOEvents.DESIGNATE_HQ_RESULT,
      mmo_set_tax_rate_result: MMOEvents.SET_TAX_RATE_RESULT,
      mmo_deploy_structure_result: MMOEvents.DEPLOY_STRUCTURE_RESULT,
      mmo_repair_structure_result: MMOEvents.REPAIR_STRUCTURE_RESULT,
    };

    return eventMap[type] || null;
  }

  /**
   * Authenticate with server
   */
  private authenticate(sessionToken: string): void {
    console.log('[MMONetwork] Authenticating...');
    this.send({
      type: 'auth',
      payload: { sessionId: sessionToken },
    });
  }

  /**
   * Schedule reconnection attempt
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.error('[MMONetwork] Max reconnection attempts reached');
      EventBus.emit(MMOEvents.RECONNECT_FAILED);
      return;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    this.reconnectAttempts++;
    console.log(
      `[MMONetwork] Reconnecting in ${RECONNECT_DELAY}ms (attempt ${this.reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`,
    );

    this.reconnectTimeout = setTimeout(() => {
      this.attemptConnection();
    }, RECONNECT_DELAY);
  }

  /**
   * Start ping interval to keep connection alive
   */
  private startPingInterval(): void {
    this.stopPingInterval();
    this.pingInterval = setInterval(() => {
      if (this.isConnected) {
        this.send({ type: 'ping' });
      }
    }, PING_INTERVAL);
  }

  /**
   * Stop ping interval
   */
  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Start a watchdog timer after sending a ping on tab focus.
   * If no pong arrives within the grace window, the socket is dead — force reconnect.
   */
  private startPongWatchdog(): void {
    this.clearPongWatchdog();
    this.pongWatchdog = setTimeout(() => {
      console.warn('[MMONetwork] Pong watchdog expired — connection dead, forcing reconnect');
      // Tear down the zombie socket
      this.isConnected = false;
      this.isAuthenticated = false;
      this.stopPingInterval();
      if (this.socket) {
        this.socket.onopen = null;
        this.socket.onclose = null;
        this.socket.onerror = null;
        this.socket.onmessage = null;
        try { this.socket.close(); } catch (_e) { /* ignore */ }
        this.socket = null;
      }
      if (this.sessionToken) {
        this.reconnect();
      }
    }, PONG_WATCHDOG_TIMEOUT);
  }

  /**
   * Clear the pong watchdog timer (called when pong is received).
   */
  private clearPongWatchdog(): void {
    if (this.pongWatchdog) {
      clearTimeout(this.pongWatchdog);
      this.pongWatchdog = null;
    }
  }

  /**
   * Send message to server
   */
  private send(message: BaseMessage): void {
    if (!this.socket || !this.isConnected) {
      console.warn('[MMONetwork] Cannot send message: not connected', {
        hasSocket: !!this.socket,
        isConnected: this.isConnected,
      });
      return;
    }

    if (this.socket.readyState !== WebSocket.OPEN) {
      console.warn('[MMONetwork] Cannot send message: socket not open', { readyState: this.socket.readyState });
      return;
    }

    try {
      const msgStr = JSON.stringify(message);
      this.socket.send(msgStr);
    } catch (error) {
      console.error('[MMONetwork] Failed to send message:', error);
    }
  }

  /**
   * Register message handler
   */
  public onMessage(type: string, handler: (payload: any) => void): void {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, []);
    }
    this.messageHandlers.get(type)!.push(handler);
  }

  /**
   * Unregister message handler
   */
  public offMessage(type: string, handler: (payload: any) => void): void {
    const handlers = this.messageHandlers.get(type);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Disconnect from server
   */
  public disconnect(): void {
    console.log('[MMONetwork] Disconnecting...');

    // Increment version so stale onclose callbacks are ignored
    this.connectionVersion++;

    this.stopPingInterval();
    this.clearPongWatchdog();

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.socket) {
      // Null out handlers before closing to prevent stale callbacks
      this.socket.onopen = null;
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.onmessage = null;
      this.socket.close();
      this.socket = null;
    }

    this.isConnected = false;
    this.isAuthenticated = false;
    this.reconnectAttempts = 0;
  }

  /**
   * Reconnect after disconnect (called from UI overlay).
   * Resets state and attempts a fresh connection, auto-rejoining the world on success.
   */
  public reconnect(): void {
    this.reconnectAttempts = 0;
    this.disconnectReason = null;
    this.pendingRejoin = true;
    this.attemptConnection();
  }

  /**
   * Dispose of the manager and all its resources.
   * Disconnects from server and cleans up all event listeners.
   */
  public dispose(): void {
    if (this._isDisposed) return;
    this._isDisposed = true;

    console.log('[MMONetwork] Disposing...');

    // Disconnect from server (clears timers and socket)
    this.disconnect();

    // Clear all message handlers
    this.messageHandlers.clear();

    // Clean up all tracked event listeners
    this.disposables.dispose();

    console.log('[MMONetwork] Disposed');
  }

  // ============ MMO Action Methods ============

  /**
   * Join MMO world
   */
  public sendJoinWorld(worldId: number): void {
    console.log(`[MMONetwork] Joining world ${worldId}`);
    this.send({
      type: 'mmo_join_world',
      payload: { worldId },
    });
  }

  /**
   * Move unit
   */
  public sendMoveUnit(unitId: string, targetHex: HexCoord): void {
    console.log(`[MMONetwork] Move unit ${unitId} to (${targetHex.q}, ${targetHex.r})`);
    console.log(
      `[MMONetwork] Connection status: connected=${this.isConnected}, authenticated=${this.isAuthenticated}, readyState=${this.socket?.readyState}`,
    );
    this.send({
      type: 'mmo_move_unit',
      payload: { unitId, targetHex },
    });
  }

  /**
   * Return unit to nearest friendly station (auto-dock on arrival)
   */
  public sendReturnToBase(unitId: string): void {
    console.log(`[MMONetwork] Return to base: unit ${unitId}`);
    this.send({
      type: 'mmo_return_to_base',
      payload: { unitId },
    });
  }

  /**
   * Build unit on planet
   */
  public sendBuildUnit(planetId: string, unitType: string, platformId?: string, designId?: string): void {
    console.log(`[MMONetwork] Build ${unitType} on planet ${planetId}${platformId ? ` (platform ${platformId})` : ''}${designId ? ` (design ${designId})` : ''}`);
    const payload: Record<string, string> = { planetId, unitType };
    if (platformId) payload.platformId = platformId;
    if (designId) payload.designId = designId;
    this.send({
      type: 'mmo_build_unit',
      payload,
    });
  }

  /**
   * Attack target
   */
  public sendAttack(attackerId: string, targetId: string, position: HexCoord): void {
    console.log(`[MMONetwork] Attack: ${attackerId} -> ${targetId} at (${position.q}, ${position.r})`);
    this.send({
      type: 'mmo_attack',
      payload: { attackerId, targetId, position },
    });
  }

  /**
   * Disengage a unit from all active combat engagements
   */
  public sendDisengage(unitId: string): void {
    console.log(`[MMONetwork] Disengage: ${unitId}`);
    this.send({
      type: 'mmo_disengage',
      payload: { unitId },
    });
  }

  /**
   * Upgrade mining facility
   */
  public sendUpgradeMining(asteroidId: string): void {
    console.log(`[MMONetwork] Upgrading mining on asteroid ${asteroidId}`);
    this.send({
      type: 'mmo_upgrade_mining',
      payload: { asteroidId },
    });
  }

  /**
   * Mine an asteroid (unit must be within 1 hex)
   */
  public sendMineAsteroid(unitId: string, asteroidId: string): void {
    this.send({
      type: 'mmo_mine_asteroid',
      payload: { unitId, asteroidId },
    });
  }

  public sendStopMining(unitId: string): void {
    this.send({
      type: 'mmo_stop_mining',
      payload: { unitId },
    });
  }

  /**
   * Repair unit from cargo minerals
   */
  public sendRepairUnit(unitId: string): void {
    this.send({
      type: 'mmo_repair_unit',
      payload: { unitId },
    });
  }

  /**
   * Request chunk data for specific chunk coordinates
   */
  public sendRequestChunks(chunks: Array<{ chunkX: number; chunkY: number }>): void {
    console.log(`[MMONetwork] Requesting ${chunks.length} chunks`);
    this.send({
      type: 'mmo_request_chunks',
      payload: { chunks },
    });
  }

  /**
   * Dock unit at planet
   */
  public sendDockUnit(unitId: string, planetId: string): void {
    console.log(`[MMONetwork] Docking unit ${unitId} at planet ${planetId}`);
    this.send({
      type: 'mmo_dock_unit',
      payload: { unitId, planetId },
    });
  }

  /**
   * Dock unit at station
   */
  public sendDockAtStation(unitId: string, stationId: string): void {
    console.log(`[MMONetwork] Docking unit ${unitId} at station ${stationId}`);
    this.send({
      type: 'mmo_dock_unit',
      payload: { unitId, stationId },
    });
  }

  /**
   * Dock unit at orbital platform
   */
  public sendDockAtPlatform(unitId: string, platformId: string): void {
    console.log(`[MMONetwork] Docking unit ${unitId} at platform ${platformId}`);
    this.send({
      type: 'mmo_dock_unit',
      payload: { unitId, platformId },
    });
  }

  /**
   * Dock unit at Alien Leviathan
   */
  public sendDockAlien(unitId: string, alienUnitId: string): void {
    console.log(`[MMONetwork] Docking unit ${unitId} at Alien Leviathan ${alienUnitId}`);
    this.send({
      type: 'mmo_dock_unit',
      payload: { unitId, alienUnitId },
    });
  }

  /**
   * Undock unit from planet
   */
  public sendUndockUnit(unitId: string): void {
    console.log(`[MMONetwork] Undocking unit ${unitId}`);
    this.send({
      type: 'mmo_undock_unit',
      payload: { unitId },
    });
  }

  /**
   * Deploy an orbital platform from inventory
   */
  public sendDeployPlatform(platformId: string, q: number, r: number): void {
    console.log(`[MMONetwork] Deploying platform ${platformId} at (${q},${r})`);
    this.send({
      type: 'mmo_deploy_platform',
      payload: { platformId, q, r },
    });
  }

  /**
   * Relocate an orbital platform
   */
  public sendRelocatePlatform(platformId: string, q: number, r: number): void {
    console.log(`[MMONetwork] Relocating platform ${platformId} to (${q},${r})`);
    this.send({
      type: 'mmo_relocate_platform',
      payload: { platformId, q, r },
    });
  }

  /**
   * Activate an ability on a unit
   */
  public sendActivateAbility(unitId: string, abilityId: number, targetHex?: HexCoord): void {
    console.log(`[MMONetwork] Activating ability ${abilityId} on unit ${unitId}`);
    this.send({
      type: 'mmo_activate_ability',
      payload: { unitId, abilityId, targetHex },
    });
  }

  /**
   * Create alliance
   */
  public sendAllianceCreate(name: string, tag: string, description?: string): void {
    console.log(`[MMONetwork] Creating alliance: ${name} [${tag}]`);
    this.send({
      type: 'mmo_alliance_create',
      payload: { name, tag, description },
    });
  }

  /**
   * Rename alliance (leader only, 100 ISD)
   */
  public sendAllianceRename(name: string): void {
    console.log(`[MMONetwork] Renaming alliance to: ${name}`);
    this.send({
      type: 'mmo_alliance_rename',
      payload: { name },
    });
  }

  /**
   * Leave alliance
   */
  public sendAllianceLeave(): void {
    console.log(`[MMONetwork] Leaving alliance`);
    this.send({
      type: 'mmo_alliance_leave',
      payload: {},
    });
  }

  /**
   * Request list of all alliances in the world
   */
  public sendAllianceList(): void {
    console.log(`[MMONetwork] Requesting alliance list`);
    this.send({
      type: 'mmo_alliance_list',
      payload: {},
    });
  }

  /**
   * Join an alliance by ID
   */
  public sendAllianceJoin(allianceId: number): void {
    console.log(`[MMONetwork] Joining alliance ${allianceId}`);
    this.send({
      type: 'mmo_alliance_join',
      payload: { allianceId },
    });
  }

  /**
   * Request alliance details (real stats for alliance panel)
   */
  public sendGetAllianceDetails(): void {
    console.log(`[MMONetwork] Requesting alliance details`);
    this.send({
      type: 'mmo_get_alliance_details',
      payload: {},
    });
  }

  /**
   * Send global chat message
   */
  public sendGlobalChat(message: string): void {
    console.log(`[MMONetwork] Global chat: ${message}`);
    this.send({
      type: 'mmo_chat_message',
      payload: { channel: 'global', message },
    });
  }

  /**
   * Send alliance chat message
   */
  public sendAllianceChat(message: string): void {
    console.log(`[MMONetwork] Alliance chat: ${message}`);
    this.send({
      type: 'mmo_alliance_chat',
      payload: { message },
    });
  }

  /**
   * Send direct message
   */
  public sendDirectMessage(recipientId: string, message: string): void {
    console.log(`[MMONetwork] DM to ${recipientId}: ${message}`);
    this.send({
      type: 'mmo_direct_message',
      payload: { recipientId, message },
    });
  }

  /**
   * Get the current session token (for REST API calls that need auth).
   */
  public getSessionToken(): string | null {
    return this.sessionToken;
  }

  /**
   * Get connection status
   */
  public getStatus(): { connected: boolean; authenticated: boolean } {
    return {
      connected: this.isConnected,
      authenticated: this.isAuthenticated,
    };
  }

  /**
   * Get current reconnect attempt number
   */
  public getReconnectAttempts(): number {
    return this.reconnectAttempts;
  }

  /**
   * Set server URL (must be called before connect)
   */
  public sendVisibilityChange(visible: boolean): void {
    this.send({
      type: 'mmo_client_visibility',
      payload: { visible },
    });
  }

  public sendClaimWreck(unitId: string, wreckId: string): void {
    this.send({
      type: 'mmo_claim_wreck',
      payload: { unitId, wreckId },
    });
  }

  // --- Burn Sink Actions ---

  public sendNameShip(unitId: string, name: string): void {
    this.send({ type: 'mmo_name_ship', payload: { unitId, name } });
  }

  public sendPaintShip(unitId: string, colorTint: number): void {
    this.send({ type: 'mmo_paint_ship', payload: { unitId, colorTint } });
  }

  public sendPriorityDeliver(unitId: string): void {
    this.send({ type: 'mmo_priority_deliver', payload: { unitId } });
  }

  public sendSensorBoost(unitId: string): void {
    this.send({ type: 'mmo_sensor_boost', payload: { unitId } });
  }

  public sendWarpJump(unitId: string, targetQ: number, targetR: number): void {
    this.send({ type: 'mmo_warp_jump', payload: { unitId, targetQ, targetR } });
  }

  public sendPlaceBeacon(unitId: string): void {
    this.send({ type: 'mmo_place_beacon', payload: { unitId } });
  }

  public sendWarpToBeacon(unitId: string, beaconId: string): void {
    this.send({ type: 'mmo_warp_beacon', payload: { unitId, beaconId } });
  }

  /**
   * Request leaderboard data from server
   */
  public requestLeaderboard(): void {
    console.log(`[MMONetwork] Requesting leaderboard`);
    this.send({
      type: 'mmo_get_leaderboard',
      payload: {},
    });
  }

  /**
   * Designate a station as alliance HQ
   */
  public sendDesignateHQ(stationId: string): void {
    console.log(`[MMONetwork] Designating HQ: ${stationId}`);
    this.send({
      type: 'mmo_designate_hq',
      payload: { stationId },
    });
  }

  /**
   * Set the alliance tax rate (0-50)
   */
  public sendSetTaxRate(rate: number): void {
    console.log(`[MMONetwork] Setting tax rate: ${rate}%`);
    this.send({
      type: 'mmo_set_tax_rate',
      payload: { rate },
    });
  }

  /**
   * Deploy a structure at a hex position
   */
  public sendDeployStructure(type: string, q: number, r: number): void {
    console.log(`[MMONetwork] Deploying structure ${type} at (${q},${r})`);
    this.send({
      type: 'mmo_deploy_structure',
      payload: { type, q, r },
    });
  }

  /**
   * Repair a damaged structure
   */
  public sendRepairStructure(structureId: string): void {
    console.log(`[MMONetwork] Repairing structure: ${structureId}`);
    this.send({
      type: 'mmo_repair_structure',
      payload: { structureId },
    });
  }

  public setServerUrl(url: string): void {
    if (this.isConnected) {
      console.warn('[MMONetwork] Cannot change server URL while connected');
      return;
    }
    this.serverUrl = url;
  }
}
